import { useState } from 'react';
import { Share2, Copy, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { axiosInstance } from '@/utils/axiosConfig';
import { QRCodeSVG } from 'qrcode.react';
import { showToast } from '@/utils/toast';

interface InviteMemberProps {
  organizationId: string;
  organizationName: string;
  trigger?: React.ReactNode;
  onInviteSent?: () => void;
}

export const InviteMember = ({
  organizationId,
  organizationName,
  trigger,
  onInviteSent
}: InviteMemberProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateInviteLink = async () => {
    try {
      if (!inviteEmail) {
        showToast.error({
          description: t('organizations.emailRequired')
        });
        return;
      }

      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.post(`/organizations/${organizationId}/invite-link`, {
        email: inviteEmail
      });
      
      setInviteUrl(response.data.inviteUrl);

      if (!response.data.emailSent) {
        showToast.warning({
          description: t('account.inviteLinkGeneratedButEmailFailed')
        });
      } else {
        showToast.success({
          description: t('organizations.inviteLinkGenerated')
        });
      }
    } catch (error) {
      console.error('Error generating invite link:', error);
      const axiosError = error as AxiosError<{message: string}>;
      showToast.error({
        description: axiosError.response?.data?.message || t('account.failedToGenerateInviteLink')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailInvite = async () => {
    try {
      if (!inviteEmail) {
        setError(t('organizations.emailRequired'));
        return;
      }

      setLoading(true);
      setError(null);

      if (!inviteUrl) {
        await handleGenerateInviteLink();
        return;
      }

      await axiosInstance.post(`/organizations/${organizationId}/invites`, {
        email: inviteEmail,
      });

      setOpen(false);
      onInviteSent?.();

      showToast.success({
        description: t('account.invitationEmailSentSuccessfully')
      });
    } catch (error) {
      console.error('Error sending invite:', error);
      const axiosError = error as AxiosError<{message: string}>;
      showToast.error({
        description: axiosError.response?.data?.message || t('organizations.failedToSendInvite')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const defaultTrigger = (
    <Button variant="outline">
      <Share2 className="w-4 h-4 mr-2" />
      {t('account.inviteMembers')}
    </Button>
  );

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          setInviteUrl('');
          setInviteEmail('');
          setCopied(false);
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('organizations.inviteMembersTo')} {organizationName}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Email Input */}
          <div className="space-y-2">
            <Label>{t('auth.email')}</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('organizations.enterEmailAddress')}
            />
          </div>

          {/* Generate Link Button */}
          <Button
            className="w-full"
            onClick={handleGenerateInviteLink}
            disabled={!inviteEmail || loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('organizations.generating')}
              </>
            ) : (
              t('organizations.generateInviteLink')
            )}
          </Button>

          {/* Invite URL and QR Code Section */}
          {inviteUrl && (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>{t('organizations.invitationLink')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={inviteUrl}
                    readOnly
                    disabled
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    title="Copy to clipboard"
                  >
                    <Copy className={copied ? "text-green-500" : "w-4 h-4"} />
                  </Button>
                </div>
                {copied && (
                  <p className="text-sm text-green-600">
                    {t('organizations.copiedToClipboard')}
                  </p>
                )}
              </div>

              {/* QR Code */}
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="flex justify-center mb-4">
                  <QRCodeSVG
                    value={inviteUrl}
                    size={200}
                    level="H"
                    includeMargin
                  />
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  {t('organizations.scanQRCodeToJoin')}
                </p>
              </div>

              {/* Send Email Button */}
              <Button
                className="w-full"
                onClick={handleEmailInvite}
                variant="secondary"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('organizations.sendInvitationViaEmail')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}; 